/* product-grid-section.js - Isolated and conflict-free */

(function() {
  'use strict';
  
  const MOBILE_MAX = 767;
  const RESUME_DELAY_MS = 2000;
  /** Idle pause after each seamless loop wrap before scrolling continues */
  const LOOP_GAP_MS = 2000;
  const MARQUEE_CLONE_CLASS = 'product-item--marquee-clone';
  
  function isMobileViewport() {
    return window.innerWidth <= MOBILE_MAX;
  }
  
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  
  function speedSettingToPxPerSec(speed) {
    const n = Math.max(1, Math.min(10, Number(speed) || 5));
    return 15 + (n - 1) * (65 / 9);
  }

  const ProductGridComponent = {
    resizeTimeout: null,
    zoomTimeout: null,
    
    init: function() {
      this.initProductGrids();
    },
    
    initProductGrids: function() {
      const productGridSections = document.querySelectorAll('.product-grid-section');
      
      productGridSections.forEach(section => {
        if (section._pgAbortController) {
          section._pgAbortController.abort();
          section._pgAbortController = null;
        }
        
        const abortController = new AbortController();
        section._pgAbortController = abortController;
        const signal = abortController.signal;
        
        const grid = section.querySelector('.product-grid');
        if (!grid) return;
        
        const marqueePause = { fn: function() {} };
        const marqueeResume = { fn: function() {} };
        
        this.makeScrollable(grid, signal, marqueePause, marqueeResume);
        
        const desktop = window.innerWidth >= 768;
        
        if (desktop) {
          this.setupDesktopNavigation(section, grid, signal);
          this.setupClickZoom(grid, signal);
          this.setupHoverEffects(grid, signal);
        } else {
          this.setupMobileMarquee(section, grid, signal, marqueePause, marqueeResume);
        }
      });
    },
    
    makeScrollable: function(element, signal, marqueePauseRef, marqueeResumeRef) {
      let isDown = false;
      let startX;
      let scrollLeft;
      let hasMoved = false;
      
      element.addEventListener('mousedown', (e) => {
        if (e.target.closest('.grid-arrow')) return;
        marqueePauseRef.fn();
        isDown = true;
        startX = e.pageX - element.offsetLeft;
        scrollLeft = element.scrollLeft;
        hasMoved = false;
        element.style.cursor = 'grabbing';
      }, { signal });
      
      element.addEventListener('mouseleave', () => {
        if (isDown) marqueeResumeRef.fn();
        isDown = false;
        element.style.cursor = 'grab';
      }, { signal });
      
      element.addEventListener('mouseup', () => {
        if (isDown) marqueeResumeRef.fn();
        isDown = false;
        element.style.cursor = 'grab';
      }, { signal });
      
      element.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        hasMoved = true;
        const x = e.pageX - element.offsetLeft;
        const walk = (x - startX) * 2;
        element.scrollLeft = scrollLeft - walk;
      }, { signal });
      
      let touchStartX = 0;
      let touchScrollLeft = 0;
      
      element.addEventListener('touchstart', (e) => {
        marqueePauseRef.fn();
        touchStartX = e.touches[0].pageX;
        touchScrollLeft = element.scrollLeft;
        hasMoved = false;
      }, { signal, passive: true });
      
      element.addEventListener('touchmove', (e) => {
        if (e.touches.length > 0) {
          hasMoved = true;
          const x = e.touches[0].pageX;
          const walk = (touchStartX - x) * 1.5;
          element.scrollLeft = touchScrollLeft + walk;
        }
      }, { signal, passive: true });
      
      element.addEventListener('touchend', () => {
        marqueeResumeRef.fn();
      }, { signal, passive: true });
      
      element.addEventListener('touchcancel', () => {
        marqueeResumeRef.fn();
      }, { signal, passive: true });
      
      element.addEventListener('click', (e) => {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, { capture: true, signal });
    },
    
    teardownMarqueeClones: function(grid) {
      grid.querySelectorAll('.' + MARQUEE_CLONE_CLASS).forEach(function(node) {
        node.remove();
      });
      grid.classList.remove('product-grid--marquee');
    },
    
    setupMobileMarquee: function(section, grid, signal, marqueePauseRef, marqueeResumeRef) {
      const enabled = grid.dataset.mobileAutoscroll === 'true';
      if (!enabled || prefersReducedMotion() || !isMobileViewport()) {
        return;
      }
      
      const originals = grid.querySelectorAll('.product-item:not(.' + MARQUEE_CLONE_CLASS + ')');
      if (!originals.length) {
        return;
      }
      
      this.teardownMarqueeClones(grid);
      
      const widthBeforeClone = grid.scrollWidth;
      if (widthBeforeClone <= grid.clientWidth + 1) {
        return;
      }
      
      originals.forEach(function(item) {
        const clone = item.cloneNode(true);
        clone.classList.add(MARQUEE_CLONE_CLASS);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach(function(el) {
          el.removeAttribute('id');
        });
        grid.appendChild(clone);
      });
      
      grid.classList.add('product-grid--marquee');
      
      // Flex `gap` breaks scrollWidth/2 — gap count between halves is not half of total gaps.
      void grid.offsetHeight;
      var firstClone = grid.querySelector('.' + MARQUEE_CLONE_CLASS);
      var segmentWidth = firstClone ? Math.round(firstClone.offsetLeft) : 0;
      if (!segmentWidth || segmentWidth < 2) {
        segmentWidth = Math.round(grid.scrollWidth / 2);
      }
      if (segmentWidth <= grid.clientWidth + 1) {
        this.teardownMarqueeClones(grid);
        return;
      }
      
      const speed = speedSettingToPxPerSec(grid.dataset.autoscrollSpeed);
      let rafId = 0;
      let lastTs = 0;
      let paused = false;
      let resumeTimer = null;
      let loopGapUntil = 0;
      
      function cancelResumeTimer() {
        if (resumeTimer) {
          clearTimeout(resumeTimer);
          resumeTimer = null;
        }
      }
      
      function pause() {
        paused = true;
        cancelResumeTimer();
      }
      
      function scheduleResume() {
        cancelResumeTimer();
        resumeTimer = setTimeout(function() {
          resumeTimer = null;
          paused = false;
        }, RESUME_DELAY_MS);
      }
      
      marqueePauseRef.fn = pause;
      marqueeResumeRef.fn = scheduleResume;
      
      function tick(ts) {
        if (signal.aborted) return;
        var prev = lastTs || ts;
        lastTs = ts;
        var dt = Math.min(0.1, (ts - prev) / 1000);
        
        if (!paused && performance.now() >= loopGapUntil) {
          grid.scrollLeft += speed * dt;
          var didWrap = false;
          while (grid.scrollLeft >= segmentWidth - 0.5) {
            grid.scrollLeft -= segmentWidth;
            didWrap = true;
          }
          if (didWrap) {
            loopGapUntil = performance.now() + LOOP_GAP_MS;
          }
        }
        
        rafId = requestAnimationFrame(tick);
      }
      
      rafId = requestAnimationFrame(tick);
      
      function cleanup() {
        cancelResumeTimer();
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
        if (grid.isConnected) {
          ProductGridComponent.teardownMarqueeClones(grid);
        }
        marqueePauseRef.fn = function() {};
        marqueeResumeRef.fn = function() {};
      }
      
      signal.addEventListener('abort', cleanup, { once: true });
    },
    
    setupDesktopNavigation: function(section, grid, signal) {
      const leftArrow = section.querySelector('.grid-arrow-left');
      const rightArrow = section.querySelector('.grid-arrow-right');
      
      if (!leftArrow || !rightArrow) return;
      
      const getScrollAmount = () => {
        const firstItem = grid.querySelector('.product-item:not(.' + MARQUEE_CLONE_CLASS + ')');
        if (firstItem) {
          return firstItem.offsetWidth + 15;
        }
        return 200;
      };
      
      const updateArrowStates = () => {
        const tolerance = 5;
        const isAtStart = grid.scrollLeft <= tolerance;
        const isAtEnd = grid.scrollLeft >= (grid.scrollWidth - grid.clientWidth - tolerance);
        const hasOverflow = grid.scrollWidth > grid.clientWidth;
        
        if (hasOverflow) {
          leftArrow.style.display = 'flex';
          rightArrow.style.display = 'flex';
        } else {
          leftArrow.style.display = 'none';
          rightArrow.style.display = 'none';
        }
        
        leftArrow.disabled = isAtStart;
        rightArrow.disabled = isAtEnd;
        
        leftArrow.style.opacity = isAtStart ? '0.4' : '1';
        rightArrow.style.opacity = isAtEnd ? '0.4' : '1';
      };
      
      leftArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!leftArrow.disabled) {
          const scrollAmount = getScrollAmount();
          grid.scrollBy({
            left: -scrollAmount,
            behavior: 'smooth'
          });
        }
      }, { signal });
      
      rightArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!rightArrow.disabled) {
          const scrollAmount = getScrollAmount();
          grid.scrollBy({
            left: scrollAmount,
            behavior: 'smooth'
          });
        }
      }, { signal });
      
      grid.addEventListener('scroll', updateArrowStates, { signal });
      
      setTimeout(updateArrowStates, 100);
      
      const images = grid.querySelectorAll('img');
      images.forEach(img => {
        const updateOnLoad = () => setTimeout(updateArrowStates, 50);
        
        if (img.complete) {
          updateOnLoad();
        } else {
          img.addEventListener('load', updateOnLoad, { signal });
        }
      });
      
      section.addEventListener('keydown', (e) => {
        if (document.activeElement && section.contains(document.activeElement)) {
          if (e.key === 'ArrowLeft' && !leftArrow.disabled) {
            e.preventDefault();
            leftArrow.click();
          } else if (e.key === 'ArrowRight' && !rightArrow.disabled) {
            e.preventDefault();
            rightArrow.click();
          }
        }
      }, { signal });
    },
    
    setupClickZoom: function(grid, signal) {
      const productItems = grid.querySelectorAll('.product-item:not(.' + MARQUEE_CLONE_CLASS + ')');
      const self = this;
      
      productItems.forEach(item => {
        const productLink = item.querySelector('.product-link');
        
        item.addEventListener('click', (e) => {
          if (e.defaultPrevented || e.target.closest('.grid-arrow')) return;
          
          e.preventDefault();
          
          if (self.zoomTimeout) {
            clearTimeout(self.zoomTimeout);
          }
          
          const isCurrentlyZoomed = item.classList.contains('zoomed');
          
          productItems.forEach(otherItem => {
            otherItem.classList.remove('zoomed', 'zoom-out');
          });
          
          if (!isCurrentlyZoomed) {
            item.classList.add('zoomed');
            
            self.zoomTimeout = setTimeout(() => {
              item.classList.add('zoom-out');
              item.classList.remove('zoomed');
              
              setTimeout(() => {
                item.classList.remove('zoom-out');
              }, 300);
            }, 0);
            
            if (productLink && productLink.href && productLink.href !== '#' && productLink.href !== window.location.href + '#') {
              setTimeout(() => {
                window.location.href = productLink.href;
              }, 400);
            }
          } else {
            if (productLink && productLink.href && productLink.href !== '#' && productLink.href !== window.location.href + '#') {
              window.location.href = productLink.href;
            }
          }
        }, { signal });
      });
      
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.product-grid-section')) {
          productItems.forEach(item => {
            item.classList.remove('zoomed', 'zoom-out');
          });
          if (self.zoomTimeout) {
            clearTimeout(self.zoomTimeout);
          }
        }
      }, { signal });
    },
    
    setupHoverEffects: function(grid, signal) {
      const productItems = grid.querySelectorAll('.product-item:not(.' + MARQUEE_CLONE_CLASS + ')');
      
      productItems.forEach(item => {
        const image = item.querySelector('.product-image');
        const title = item.querySelector('.product-title');
        
        item.addEventListener('mouseenter', () => {
          if (!item.classList.contains('zoomed')) {
            if (image) {
              image.style.transform = 'scale(1.05)';
            }
            if (title) {
              title.style.color = '#ff0000';
              title.style.transform = 'scale(1.05)';
            }
          }
        }, { signal });
        
        item.addEventListener('mouseleave', () => {
          if (!item.classList.contains('zoomed')) {
            if (image) {
              image.style.transform = 'scale(1)';
            }
            if (title) {
              title.style.color = '#626262';
              title.style.transform = 'scale(1)';
            }
          }
        }, { signal });
      });
    },
    
    handleResize: function() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.initProductGrids();
      }, 250);
    }
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      ProductGridComponent.init();
    });
  } else {
    ProductGridComponent.init();
  }
  
  window.addEventListener('resize', function() {
    ProductGridComponent.handleResize();
  });
  
  window.addEventListener('orientationchange', function() {
    setTimeout(() => {
      ProductGridComponent.handleResize();
    }, 100);
  });
  
  document.addEventListener('DOMContentLoaded', function() {
    const productGridSections = document.querySelectorAll('.product-grid-section');
    
    productGridSections.forEach(section => {
      const images = section.querySelectorAll('.product-image');
      images.forEach(img => {
        img.addEventListener('error', function() {
          this.style.display = 'none';
          const container = this.parentNode;
          let placeholder = container.querySelector('.product-image-placeholder');
          
          if (!placeholder) {
            placeholder = document.createElement('div');
            placeholder.className = 'product-image-placeholder';
            container.appendChild(placeholder);
          }
          placeholder.style.display = 'block';
        });
      });
    });
  });
  
  if (typeof window.ProductGridComponent === 'undefined') {
    window.ProductGridComponent = ProductGridComponent;
  }
  
})();
